/**
 * Connect-level user preferences on the one account config path (I-2510):
 * keys of master's `/v1/account/configs`, read and written through
 * `useAccountConfig` (@blpde/connect).
 *
 * - `theme`      `{mode: 'light' | 'dark'}` — orthogonal to the look: every
 *                look has both palettes, `<html data-theme>` picks one.
 * - `locale`     `{locale: 'de' | 'en'}`.
 * - `appearance` `{active, allow_personal}` — the active appearance (a
 *                built-in catalog id or a stored appearance's id); the shell
 *                only ever writes `active`, `allow_personal` is the admins'.
 * - `look`       the colours, type, shape and the look's design axes
 *                (surface, density) master resolved for the session (see
 *                `appearance/look.ts`).
 * - `form`       the form axes (input, reveal, inputicon, savestatus), applied
 *                with the look's as the kit's `data-nx-*` attributes.
 * - `table`, `shell` are read by the kit (NxTable, the panes) and the tab bar;
 *                the shell loads them with the others so the first views
 *                need no request of their own.
 *
 * The helper's boot cache (`formica.config.<account>.<key>`) paints the first
 * frame synchronously; `loadShellConfigs()` refreshes the keys once
 * `/oauth/userinfo` confirmed the session. A setter here writes the
 * session's profile layer (the helper's default target).
 */
import { computed, shallowRef, watch, type ComputedRef, type Ref } from 'vue';
import {
  loadAccountConfigs,
  reloadAccountConfigs,
  useAccountConfig,
  type AccountConfigResult,
  type EnforcedField,
} from '@blpde/connect';
import { SHELL_LOCALE_STORAGE_KEY, type ShellLocale } from '@blpde/connect/http';
import {
  emitAppearanceDocumentCss,
  emitAppearanceDocumentLegacyCss,
  emitAppearanceSchemeCss,
  emitAppearanceSchemeLegacyCss,
  emitLegacyNxAliases,
  emitRuntimeTypeCss,
  getAppearanceScheme,
  isDefaultRuntimeType,
  parseBrandHex,
  parseRuntimeTypeConfig,
  seedsForNamedBrand,
  DEFAULT_RUNTIME_TYPE,
  PLATFORM_APPEARANCE_SCHEMES,
  RUNTIME_BRAND_STYLE_ID,
  RUNTIME_LEGACY_NX_STYLE_ID,
  RUNTIME_SEED_ROLES,
  RUNTIME_TYPE_SCALE_MAX,
  RUNTIME_TYPE_SCALE_MIN,
  RUNTIME_TYPE_SCALE_PRESETS,
  RUNTIME_TYPE_SCALE_STEP,
  RUNTIME_TYPE_STYLE_ID,
  SUPPORTED_MONO_FONTS,
  SUPPORTED_UI_FONTS,
  UI_FONT_STACKS,
  type AppearanceTokenOverrides,
  type NamedBrand,
  type RuntimeBrandSeeds,
  type RuntimeTypeConfig,
  type TokenOverrideSlot,
} from '@blpde/nx-tokens';
import {
  applyDesignState,
  designStateOf,
  formStateOf,
  useShellSurface,
  type DesignAxisName,
  type DesignState,
  type FormState,
  type LookDesign,
} from './design';
import {
  APPEARANCE_CONFIG_KEY,
  DEFAULT_APPEARANCE_ID,
  FORM_CONFIG_KEY,
  LOOK_CONFIG_KEY,
  TABLE_CONFIG_KEY,
  isBuiltinAppearanceId,
  resolveLook,
  type AppearanceLook,
  type AppearanceSelection,
  type LookConfig,
} from './appearance/look';

export const THEME_CONFIG_KEY = 'theme';
export const LOCALE_CONFIG_KEY = 'locale';
/** The shell's own UI state (the tab bar's workspaces, the kit's panes — `shell.panes.<storageKey>`). */
export const SHELL_CONFIG_KEY = 'shell';

/** The keys the shell paints with — `loadShellConfigs()` reads them in one request. */
export const SHELL_CONFIG_KEYS = [
  LOOK_CONFIG_KEY,
  APPEARANCE_CONFIG_KEY,
  THEME_CONFIG_KEY,
  LOCALE_CONFIG_KEY,
  FORM_CONFIG_KEY,
  TABLE_CONFIG_KEY,
  SHELL_CONFIG_KEY,
] as const;

/* ── Locale ─────────────────────────────────────────────────────────────── */

export type { ShellLocale };
const SUPPORTED: readonly ShellLocale[] = ['de', 'en'] as const;
const DEFAULT_LOCALE: ShellLocale = 'de';

function isLocale(value: unknown): value is ShellLocale {
  return typeof value === 'string' && (SUPPORTED as readonly string[]).includes(value);
}

const localeConfig = useAccountConfig<{ locale?: string }>(LOCALE_CONFIG_KEY, {
  fallback: { locale: DEFAULT_LOCALE },
  immediate: false,
});

const _locale = computed<ShellLocale>(() => {
  const value = localeConfig.effective.value.locale;
  return isLocale(value) ? value : DEFAULT_LOCALE;
});

// HTTP clients and the socket read the UI language at request time from the key
// the http module names; the account config is its source, so mirror it there.
watch(
  _locale,
  (value) => {
    try {
      localStorage.setItem(SHELL_LOCALE_STORAGE_KEY, value);
    } catch {
      /* storage blocked: requests fall back to the default language */
    }
  },
  { immediate: true, flush: 'sync' },
);

export function useShellLocale(): { locale: Readonly<Ref<ShellLocale>> } {
  return { locale: _locale };
}

export function setShellLocale(next: ShellLocale): void {
  if (!isLocale(next) || next === _locale.value) return;
  void localeConfig.patch({ locale: next });
}

export const supportedLocales = SUPPORTED;

/* ── Theme (light / dark) ──────────────────────────────────────────────── */

export type ShellTheme = 'light' | 'dark';
const SUPPORTED_THEMES: readonly ShellTheme[] = ['light', 'dark'] as const;

function isTheme(value: unknown): value is ShellTheme {
  return typeof value === 'string' && (SUPPORTED_THEMES as readonly string[]).includes(value);
}

const themeConfig = useAccountConfig<{ mode?: string }>(THEME_CONFIG_KEY, {
  fallback: { mode: 'light' },
  immediate: false,
});

const _theme = computed<ShellTheme>(() => {
  const value = themeConfig.effective.value.mode;
  return isTheme(value) ? value : 'light';
});

function applyTheme(theme: ShellTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
}

// Applied at module load (boot cache), so a saved dark choice paints without a flash.
watch(_theme, applyTheme, { immediate: true, flush: 'sync' });

export function useShellTheme(): { theme: Readonly<Ref<ShellTheme>> } {
  return { theme: _theme };
}

export function setShellTheme(next: ShellTheme): void {
  if (!isTheme(next) || next === _theme.value) return;
  void themeConfig.patch({ mode: next });
}

export const supportedThemes = SUPPORTED_THEMES;

/* ── Appearance (which one is active) ──────────────────────────────────── */

const appearanceConfig = useAccountConfig<AppearanceSelection>(APPEARANCE_CONFIG_KEY, {
  fallback: { active: DEFAULT_APPEARANCE_ID, allow_personal: true },
  immediate: false,
});

const _activeAppearance = computed<string>(() => {
  const active = appearanceConfig.effective.value.active;
  return typeof active === 'string' && active.trim() ? active.trim() : DEFAULT_APPEARANCE_ID;
});

const _allowPersonal = computed<boolean>(() => appearanceConfig.effective.value.allow_personal !== false);

/** The active appearance and whether personal appearances may be made. */
export function useShellAppearanceSelection(): {
  active: ComputedRef<string>;
  allowPersonal: ComputedRef<boolean>;
  enforced: Readonly<Ref<EnforcedField[]>>;
} {
  return { active: _activeAppearance, allowPersonal: _allowPersonal, enforced: appearanceConfig.enforced };
}

/* ── Look ───────────────────────────────────────────────────────────────── */

const lookConfig = useAccountConfig<LookConfig>(LOOK_CONFIG_KEY, { layers: true, immediate: false });

/**
 * A look shown while an activation is on its way: the designer hands over the
 * preview it already holds, so the shell switches at once instead of after
 * master's answer.
 */
const _pendingLook = shallowRef<{ id: string; result: AccountConfigResult<LookConfig> } | null>(null);

const _look = computed<AppearanceLook>(() => {
  const pending = _pendingLook.value;
  if (pending && pending.id === _activeAppearance.value) {
    return resolveLook({
      id: pending.id,
      effective: pending.result.effective,
      layers: pending.result.layers,
      enforced: pending.result.enforced,
    });
  }
  return resolveLook({
    id: _activeAppearance.value,
    effective: lookConfig.effective.value,
    layers: lookConfig.layers.value,
    enforced: lookConfig.enforced.value,
  });
});

/** The look of the active appearance, and the look fields a tenant or company locked. */
export function useShellAppearance(): {
  look: ComputedRef<AppearanceLook>;
  enforced: Readonly<Ref<EnforcedField[]>>;
} {
  return { look: _look, enforced: lookConfig.enforced };
}

/* ── Form ───────────────────────────────────────────────────────────────── */

const formConfig = useAccountConfig<Record<string, unknown>>(FORM_CONFIG_KEY, {
  immediate: false,
});

const _form = computed<FormState>(() => formStateOf(formConfig.effective.value));

/** The form axes (input, reveal, inputicon, savestatus) the session resolved, and the fields locked. */
export function useShellForm(): {
  form: ComputedRef<FormState>;
  enforced: Readonly<Ref<EnforcedField[]>>;
} {
  return { form: _form, enforced: formConfig.enforced };
}

// The kit's axes are the look's (surface, density) and the form's; the first
// paint comes from the boot cache of both keys.
watch(
  () => designStateOf(_look.value.design, _form.value),
  (state) => applyDesignState(state),
  { immediate: true, flush: 'sync' }
);

/* ── Applying a look to the shell ──────────────────────────────────────── */

function applyHeadStyle(id: string, css: string | null): void {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(id);
  if (!css) {
    existing?.remove();
    return;
  }
  let el = existing instanceof HTMLStyleElement ? existing : null;
  if (!el) {
    existing?.remove();
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

/** Brand + legacy CSS for a look. Returns null when the palette cannot be emitted (AA guard). */
function brandCssFor(look: AppearanceLook): { brand: string; legacy: string | null } | null {
  try {
    // A built-in exactly as the catalog draws it: the scheme's own maps.
    if (isBuiltinAppearanceId(look.id) && look.sourceScheme && !look.tokens) {
      const scheme = getAppearanceScheme(look.sourceScheme);
      if (scheme) {
        return { brand: emitAppearanceSchemeCss(scheme), legacy: emitAppearanceSchemeLegacyCss(scheme) };
      }
    }
    const doc = {
      seeds: look.seeds,
      ...(look.tokens ? { tokens: look.tokens } : {}),
      ...(look.sourceScheme ? { sourceScheme: look.sourceScheme } : {}),
    };
    let legacy: string | null = null;
    try {
      legacy = emitAppearanceDocumentLegacyCss(doc);
    } catch {
      try {
        legacy = emitLegacyNxAliases(look.seeds);
      } catch {
        legacy = null;
      }
    }
    return { brand: emitAppearanceDocumentCss(doc), legacy };
  } catch {
    return null;
  }
}

/** Can this look be applied to the shell (palette passes the AA guard)? */
export function canApplyAppearanceLook(look: AppearanceLook): boolean {
  return brandCssFor(look) != null;
}

function applyTypeCss(config: RuntimeTypeConfig): boolean {
  const parsed = parseRuntimeTypeConfig(config);
  if (!parsed) return false;
  if (isDefaultRuntimeType(parsed)) {
    applyHeadStyle(RUNTIME_TYPE_STYLE_ID, null);
    return true;
  }
  try {
    applyHeadStyle(RUNTIME_TYPE_STYLE_ID, emitRuntimeTypeCss(parsed));
  } catch {
    return false;
  }
  return true;
}

/**
 * Pushes a look onto the shell: unlayered role CSS, legacy `--nx-brand-rgb`
 * and typography CSS (the design axes follow the look and the form in the
 * watch above). Returns false — and changes nothing — when the palette fails
 * the AA guard.
 */
function applyLookToShell(look: AppearanceLook): boolean {
  if (typeof document === 'undefined') return false;
  const css = brandCssFor(look);
  if (!css) return false;
  document.documentElement.removeAttribute('data-brand');
  applyHeadStyle(RUNTIME_BRAND_STYLE_ID, css.brand);
  applyHeadStyle(RUNTIME_LEGACY_NX_STYLE_ID, css.legacy);
  // Type style stays after the brand style in <head>, so `--nxt-font*` from
  // the type config wins over a stray shared token override.
  applyTypeCss(look.type);
  return true;
}

/**
 * Paints the look the login page's branding carries (edge rebuild plan W19):
 * the public part of the look of the hostname's scope, read before anybody
 * signed in. Nothing is stored and no store is touched; signing in reloads the
 * page and the shell paints the account's own look. Returns false — and
 * changes nothing — when the palette fails the AA guard.
 */
export function applyLoginLook(doc: Record<string, unknown>): boolean {
  const look = resolveLook({ id: 'login', effective: doc });
  if (!applyLookToShell(look)) return false;
  applyDesignState(designStateOf(look.design, formStateOf({})));
  return true;
}

// The first paint comes from the boot cache; every later answer re-applies —
// once per distinct look (one answer sets several refs in a row).
let appliedLook = '';
watch(
  _look,
  (look) => {
    const signature = JSON.stringify(look);
    if (signature === appliedLook) return;
    if (applyLookToShell(look)) appliedLook = signature;
  },
  { immediate: true, flush: 'sync' }
);

/* ── Session ────────────────────────────────────────────────────────────── */

/**
 * Reads the shell's keys in one request once the session is confirmed
 * (`/oauth/userinfo`). Never throws; the boot cache stays when master is
 * unreachable.
 */
export function loadShellConfigs(): Promise<void> {
  return loadAccountConfigs(SHELL_CONFIG_KEYS);
}

/**
 * Makes an appearance the active one (`appearance.active` on the session's
 * profile layer) and reads every loaded key again — an appearance can hold a
 * layer of any key (its look, its form, its table). `preview` — the look
 * designer's `?appearance=` answer for it — switches the look at once.
 * Resolves false when master refused.
 */
export async function setShellAppearance(
  id: string,
  preview?: AccountConfigResult<LookConfig>
): Promise<boolean> {
  const next = id.trim();
  if (!next) return false;
  if (next === _activeAppearance.value && !_pendingLook.value) return true;
  if (preview) _pendingLook.value = { id: next, result: preview };
  const result = await appearanceConfig.patch({ active: next }, { immediate: true });
  if (!result) {
    _pendingLook.value = null;
    return false;
  }
  await reloadAccountConfigs();
  _pendingLook.value = null;
  return true;
}

/* ── Re-exports for views ───────────────────────────────────────────────── */

export const appearanceSchemes = PLATFORM_APPEARANCE_SCHEMES;
export const supportedUiFonts = SUPPORTED_UI_FONTS;
export const supportedMonoFonts = SUPPORTED_MONO_FONTS;
export {
  RUNTIME_BRAND_STYLE_ID,
  RUNTIME_LEGACY_NX_STYLE_ID,
  RUNTIME_TYPE_STYLE_ID,
  RUNTIME_TYPE_SCALE_MIN,
  RUNTIME_TYPE_SCALE_MAX,
  RUNTIME_TYPE_SCALE_STEP,
  RUNTIME_TYPE_SCALE_PRESETS,
  DEFAULT_RUNTIME_TYPE,
  UI_FONT_STACKS,
  seedsForNamedBrand,
  parseBrandHex,
  RUNTIME_SEED_ROLES,
  getAppearanceScheme,
  useShellSurface,
};
export type {
  AppearanceLook,
  AppearanceSelection,
  LookConfig,
  RuntimeBrandSeeds,
  RuntimeTypeConfig,
  NamedBrand,
  AppearanceTokenOverrides,
  TokenOverrideSlot,
  DesignState,
  DesignAxisName,
  FormState,
  LookDesign,
};

/** Test seam: forget a pending activation. */
export function resetShellSettingsForTests(): void {
  _pendingLook.value = null;
}
